import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import PublicChatWidget from '../components/PublicChatWidget';

/**
 * Embed mode — renders ONLY the chat widget in fullscreen, designed to be
 * loaded inside an iframe by /widget.js. Sends postMessage('docubot:close')
 * back to the parent when the user closes the widget.
 */
const WidgetEmbed = () => {
  const { subdomain } = useParams();
  const [params] = useSearchParams();
  const accessKey = params.get('key') || '';
  const colorOverride = params.get('color');
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const keyParam = accessKey ? `?key=${accessKey}` : '';
        const res = await axios.get(`${API_URL}/public/workspaces/${subdomain}${keyParam}`);
        setWorkspace(res.data);
      } catch (e) {
        // ignore — show error UI below
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [subdomain, accessKey]);

  const notifyClose = () => {
    try { window.parent.postMessage({ type: 'docubot:close' }, '*'); } catch (_) { void _; }
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-transparent">
        <div className="text-zinc-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white p-6 text-center">
        <div>
          <div className="text-zinc-800 font-bold">Helpdesk unavailable</div>
          <div className="text-xs text-zinc-500 mt-1">Workspace not found or access denied.</div>
        </div>
      </div>
    );
  }

  const settings = workspace.settings || {};
  const primaryColor = colorOverride || settings.primary_color || '#7c3aed';

  // The widget uses fixed bottom-6 right-6 — override via wrapper to fill iframe
  return (
    <div className="h-screen w-screen bg-transparent" data-testid="widget-embed-root">
      <style>{`
        [data-testid="chat-widget"] {
          position: fixed !important;
          inset: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          height: 100% !important;
          max-height: 100% !important;
          border-radius: 24px !important;
          bottom: auto !important;
          right: auto !important;
        }
      `}</style>
      <PublicChatWidget
        subdomain={subdomain}
        workspaceName={workspace.name}
        primaryColor={primaryColor}
        onClose={notifyClose}
        settings={settings}
        accessKey={accessKey}
      />
    </div>
  );
};

export default WidgetEmbed;
